<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div class="text-center">
        <h2 class="mt-6 text-3xl font-extrabold text-gray-900">Reset your password</h2>
        <p class="mt-2 text-sm text-gray-600">Enter your new password below.</p>
      </div>

      <!-- Per-status banner when reset is rejected (banned / deactivated).
           FE owns the copy; BE only signals which status was hit. -->
      <div
        v-if="disabled"
        class="rounded-md border p-4"
        :class="disabled.status === 'banned' ? 'bg-red-50 border-red-200 text-red-900' : 'bg-gray-50 border-gray-200 text-gray-900'"
      >
        <h3 class="text-sm font-semibold">Password reset unavailable</h3>
        <p class="mt-1 text-sm">
          {{
            disabled.status === 'banned'
              ? 'This account has been suspended. Contact support if you believe this is in error.'
              : 'This account is unavailable. Contact support to restore access.'
          }}
        </p>
        <a
          v-if="disabled.support_email"
          :href="`mailto:${disabled.support_email}?subject=Account%20${disabled.status}`"
          class="mt-3 inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md bg-gray-700 text-white hover:bg-gray-800"
        >
          Contact support
        </a>
      </div>

      <div v-else class="mt-8">
        <ResetPasswordForm :token="token" @account-disabled="handleAccountDisabled" />
      </div>

      <div class="text-center">
        <router-link
          to="/auth/login"
          class="font-medium text-blue-600 hover:text-blue-500 transition-colors inline-flex items-center gap-1"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back to sign in
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useAuth } from '@/features/auth/composables/useAuth';
  import { ResetPasswordForm } from '@/features/auth/components';

  const route = useRoute();
  const router = useRouter();
  const { isAuthenticated } = useAuth();

  type DisabledStatus = 'pending_approval' | 'banned' | 'suspended' | 'deactivated' | 'disabled';
  const disabled = ref<{ status: DisabledStatus; message: string; waitlist_url: string; support_email: string } | null>(null);

  const handleAccountDisabled = (info: { status: DisabledStatus; message: string; waitlist_url: string; support_email: string }) => {
    disabled.value = info;
  };

  // Get token from query params
  const token = computed(() => (route.query.token as string) || '');

  // Redirect if already authenticated or no token
  onMounted(() => {
    if (isAuthenticated.value) {
      router.push('/app');
    } else if (!token.value) {
      router.push('/auth/forgot-password');
    }
  });
</script>
